import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ApproveButton, ChallanButton, RejectButton } from './ReturnActions';
import { fmtDate, fmtQ } from './srUtils';

const STATUS = {
  PENDING: ['Waiting approval', 'bg-blue-100 text-blue-800'],
  APPROVED: ['Returned to supplier', 'bg-emerald-100 text-emerald-800'],
  REJECTED: ['Rejected', 'bg-red-100 text-red-700'],
};

const LineRow = ({ ln }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 py-2 border-t border-[#E5DFD0] first:border-t-0" data-testid={`sr-line-${ln.line_id}`}>
    <div className="min-w-0">
      <p className="font-mono text-sm font-semibold">{ln.kind === 'sort' ? `Sort ${ln.sort_no}` : ln.bundle_barcode}</p>
      <p className="text-xs text-gray-600 truncate">{[ln.kind === 'bundle' && ln.sort_no, ln.design_name, ln.color_name].filter(Boolean).join(' · ') || ln.stage}</p>
      <p className="text-xs text-gray-500">{ln.reason_label}{ln.note ? ` — ${ln.note}` : ''}</p>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {(ln.po_parts || []).map((p) => (
          <span key={p.po_item_id} className="text-[11px] text-red-700">{fmtQ(p.qty)} {p.unit} above {p.po_number}</span>
        ))}
        {ln.no_po_qty > 0 && <span className="text-[11px] text-amber-800">{fmtQ(ln.no_po_qty)} pcs not on a PO</span>}
      </div>
    </div>
    <div className="text-right">
      <p className="text-lg font-bold tabular-nums">{fmtQ(ln.qty)}</p>
      <p className="text-[11px] text-gray-500">{ln.kind === 'bundle' && ln.whole === false ? `part of ${fmtQ(ln.qty_before)}` : 'pcs'}</p>
    </div>
  </div>
);

const Footer = ({ r, canApprove, onChanged }) => {
  if (r.status === 'PENDING') {
    return r.can_approve && canApprove
      ? <div className="flex gap-2 justify-end"><RejectButton ret={r} onDone={onChanged} /><ApproveButton ret={r} onDone={onChanged} /></div>
      : <p className="text-xs text-blue-700" data-testid="sr-waiting-note">Stock is held. Waiting for Admin or the section's Incharge / Supervisor to approve.</p>;
  }
  if (r.status === 'APPROVED') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-600">Approved by {r.approved_by_name} · {fmtDate(r.approved_at)}</p>
        <ChallanButton ret={r} />
      </div>
    );
  }
  return <p className="text-xs text-red-700">Rejected by {r.rejected_by_name} · {fmtDate(r.rejected_at)} — {r.reject_reason}</p>;
};

export const ReturnCard = ({ r, canApprove, onChanged }) => {
  const [label, tone] = STATUS[r.status] || [r.status, 'bg-gray-100'];
  return (
    <div className="rounded-lg border border-[#E5DFD0] bg-white p-4 space-y-3" data-testid={`sr-return-${r.return_no.replace(/[^A-Za-z0-9]/g, '-')}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-[#1A1F2E]">{r.return_no}</span>
            <Badge variant="outline" className={`${tone} border-transparent font-medium`} data-testid="sr-return-status">{label}</Badge>
          </div>
          <p className="text-sm text-gray-700">{r.vendor_name}</p>
          <p className="text-xs text-gray-500">{r.document_barcode}{r.vendor_doc_no ? ` · Invoice ${r.vendor_doc_no}` : ''}</p>
          <p className="text-xs text-gray-500">Raised by {r.raised_by_name} · {fmtDate(r.raised_at)}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-[#B45309] tabular-nums" data-testid="sr-return-total">{fmtQ(r.total_qty)}</p>
          <p className="text-xs text-gray-500">pcs · {r.lines.length} item(s)</p>
        </div>
      </div>
      <div className="rounded-md bg-[#FFFBF5] px-3">{r.lines.map((ln) => <LineRow key={ln.line_id} ln={ln} />)}</div>
      {r.remarks && <p className="text-xs text-gray-600">Remarks: {r.remarks}</p>}
      <Footer r={r} canApprove={canApprove} onChanged={onChanged} />
    </div>
  );
};
